import React, { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import { BookOpen, Download, Image as ImageIcon, X } from 'lucide-react';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import '../styles/ComicBookExport.css';
import SYMBOLS from '../utils/symbols';

// Environment-aware logging - silent in production
const isDev = process.env.NODE_ENV !== 'production';

// Convert markdown to simple HTML for comic text
// Preserves bold and italic, strips everything else
const markdownToHtml = (text) => {
  if (!text) return '';
  return text
    // Convert bold **text** and __text__ to <strong>
    .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
    .replace(/__(.*?)__/g, '<strong>$1</strong>')
    // Convert italic *text* and _text_ to <em>
    .replace(/\*([^*]+)\*/g, '<em>$1</em>')
    .replace(/_([^_]+)_/g, '<em>$1</em>')
    // Strip headers (# ## ### etc)
    .replace(/^#{1,6}\s+/gm, '')
    // Strip code blocks
    .replace(/`(.*?)`/g, '$1')
    // Strip links but keep text
    .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
    // Clean up any stray markdown characters
    .replace(/\*\*/g, '')
    .replace(/(?<![a-zA-Z])\*(?![a-zA-Z])/g, '') // Stray * not part of words
    .trim();
};

// Component to render markdown text as HTML
const MarkdownText = ({ children }) => {
  if (!children) return null;
  return (
    <span dangerouslySetInnerHTML={{ __html: markdownToHtml(children) }} />
  );
};

// Comic page dimensions (standard US comic: 6.625" x 10.25" at 150 DPI)
const PAGE_WIDTH = 994;
const PAGE_HEIGHT = 1538;

// Panel layouts - 2x2 grid optimized for square images
const PANEL_LAYOUTS = {
  layout4: [
    { x: 2, y: 2, w: 47, h: 47 },
    { x: 51, y: 2, w: 47, h: 47 },
    { x: 2, y: 51, w: 47, h: 47 },
    { x: 51, y: 51, w: 47, h: 47 },
  ],
  layout3a: [
    { x: 2, y: 2, w: 96, h: 32 },
    { x: 2, y: 36, w: 47, h: 32 },
    { x: 51, y: 36, w: 47, h: 32 },
  ],
  layout3b: [
    { x: 2, y: 2, w: 47, h: 32 },
    { x: 51, y: 2, w: 47, h: 32 },
    { x: 2, y: 36, w: 96, h: 32 },
  ],
  layout2: [
    { x: 2, y: 2, w: 96, h: 47 },
    { x: 2, y: 51, w: 96, h: 47 },
  ],
  layout1: [
    { x: 2, y: 2, w: 96, h: 96 },
  ],
};

const ComicBookExport = ({ panels, missionResult, comicTitle, onClose }) => {
  const [isGenerating, setIsGenerating] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [exportProgress, setExportProgress] = useState(0);
  const [imagesLoaded, setImagesLoaded] = useState(false);
  const [previewScale, setPreviewScale] = useState(0.4);
  const pagesContainerRef = useRef(null);
  const previewWrapperRef = useRef(null);
  
  // Generate a stable issue number once when component mounts
  const issueNumber = useMemo(() => Math.floor(Math.random() * 999) + 1, []);

  // Calculate preview scale based on container size
  useEffect(() => {
    const calculateScale = () => {
      if (!previewWrapperRef.current) return;
      
      const container = previewWrapperRef.current;
      const containerWidth = container.clientWidth - 20; // padding
      const containerHeight = container.clientHeight - 20;
      
      // Calculate scale to fit both dimensions
      const scaleX = containerWidth / PAGE_WIDTH;
      const scaleY = containerHeight / PAGE_HEIGHT;
      const newScale = Math.min(scaleX, scaleY, 0.9); // Cap at 0.9
      
      setPreviewScale(Math.max(newScale, 0.2)); // Min 0.2 to stay readable
    };

    calculateScale();
    window.addEventListener('resize', calculateScale);
    
    // Recalculate after images load
    if (imagesLoaded) {
      setTimeout(calculateScale, 100);
    }
    
    return () => window.removeEventListener('resize', calculateScale);
  }, [imagesLoaded]);

  // Filter to only panels with images
  const storyPanels = panels.filter(p => p.role === 'assistant' && p.imageUrl);

  // Organize panels into pages
  const organizeIntoPages = useCallback(() => {
    const pages = [];
    pages.push({ type: 'cover' });
    
    const PANELS_PER_PAGE = 4;
    let currentIndex = 0;
    let pageNum = 0;
    
    while (currentIndex < storyPanels.length) {
      const remaining = storyPanels.length - currentIndex;
      const panelCount = Math.min(PANELS_PER_PAGE, remaining);
      const pagePanels = storyPanels.slice(currentIndex, currentIndex + panelCount);
      
      let layoutKey = `layout${panelCount}`;
      if (panelCount === 3) {
        layoutKey = pageNum % 2 === 0 ? 'layout3a' : 'layout3b';
      }
      
      pages.push({
        type: 'story',
        panels: pagePanels,
        layout: PANEL_LAYOUTS[layoutKey] || PANEL_LAYOUTS.layout4,
        startIndex: currentIndex,
      });
      
      currentIndex += panelCount;
      pageNum++;
    }
    
    pages.push({ type: 'back' });
    return pages;
  }, [storyPanels]);

  const comicPages = organizeIntoPages();
  const totalPages = comicPages.length;

  // Preload images
  useEffect(() => {
    const imageUrls = storyPanels.map(p => p.imageUrl).filter(Boolean);
    let loadedCount = 0;
    
    if (imageUrls.length === 0) {
      setImagesLoaded(true);
      return;
    }

    imageUrls.forEach(url => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        loadedCount++;
        if (loadedCount === imageUrls.length) setImagesLoaded(true);
      };
      img.onerror = () => {
        loadedCount++;
        if (loadedCount === imageUrls.length) setImagesLoaded(true);
      };
      img.src = url;
    });
  }, [storyPanels]);

  // Result config
  const getResultConfig = () => {
    switch (missionResult?.type) {
      case 'perfect': return { title: 'PERFECT TIMELINE', color: '#10b981', icon: SYMBOLS.STAR, success: true };
      case 'altered': return { title: 'ALTERED TIMELINE', color: '#4cc9f0', icon: SYMBOLS.DIAMOND, success: true };
      case 'innovative': return { title: 'INNOVATIVE TIMELINE', color: '#f4a261', icon: SYMBOLS.SPARKLE, success: true };
      case 'rules_broken': return { title: 'RULES BROKEN', color: '#f59e0b', icon: SYMBOLS.WARNING, success: true };
      case 'failed': return { title: 'HISTORY UNCHANGED', color: '#e94560', icon: SYMBOLS.CROSS, success: false };
      case 'corrupted': return { title: 'TIMELINE CORRUPTED', color: '#dc2626', icon: SYMBOLS.SKULL, success: false };
      case 'abandoned': return { title: 'MISSION ABANDONED', color: '#6b7280', icon: SYMBOLS.HOURGLASS, success: false };
      default: return { title: 'MISSION COMPLETE', color: '#e94560', icon: SYMBOLS.DIAMOND, success: true };
    }
  };

  const resultConfig = getResultConfig();

  // Generate PDF
  const generatePDF = useCallback(async () => {
    setIsGenerating(true);
    setExportProgress(0);

    try {
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'px',
        format: [PAGE_WIDTH, PAGE_HEIGHT],
        hotfixes: ['px_scaling'],
      });

      const pageElements = pagesContainerRef.current?.querySelectorAll('.comic-page-render');
      
      for (let i = 0; i < pageElements.length; i++) {
        await new Promise(resolve => setTimeout(resolve, 200));
        
        const canvas = await html2canvas(pageElements[i], {
          scale: 2,
          useCORS: true,
          allowTaint: true,
          backgroundColor: '#0a0a0f',
          logging: false,
          width: PAGE_WIDTH,
          height: PAGE_HEIGHT,
        });

        const imgData = canvas.toDataURL('image/jpeg', 0.95);
        if (i > 0) pdf.addPage([PAGE_WIDTH, PAGE_HEIGHT]);
        pdf.addImage(imgData, 'JPEG', 0, 0, PAGE_WIDTH, PAGE_HEIGHT);
        setExportProgress(((i + 1) / pageElements.length) * 100);
      }

      pdf.save('ribbon-rider-comic.pdf');
    } catch (error) {
      if (isDev) console.error('Error generating PDF:', error);
      alert('Error generating PDF. Please try again.');
    } finally {
      setIsGenerating(false);
    }
  }, []);

  // Download page as PNG
  const downloadPageAsPNG = useCallback(async (pageIndex) => {
    const pageElements = pagesContainerRef.current?.querySelectorAll('.comic-page-render');
    const pageEl = pageElements?.[pageIndex];
    if (!pageEl) return;

    try {
      const canvas = await html2canvas(pageEl, {
        scale: 2,
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#0a0a0f',
        width: PAGE_WIDTH,
        height: PAGE_HEIGHT,
      });

      const link = document.createElement('a');
      link.download = `ribbon-rider-page-${pageIndex + 1}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
    } catch (error) {
      if (isDev) console.error('Error downloading page:', error);
    }
  }, []);

  // COVER PAGE
  const renderCover = (forExport = false) => {
    const coverImage = storyPanels[0]?.imageUrl;
    const className = forExport ? 'comic-page comic-page-render' : 'comic-page comic-page-preview';
    
    // Split title into two lines if it has multiple words, or use as single line
    const displayTitle = comicTitle || 'UNTITLED ADVENTURE';
    const titleWords = displayTitle.split(' ');
    const midPoint = Math.ceil(titleWords.length / 2);
    const titleLine1 = titleWords.slice(0, midPoint).join(' ');
    const titleLine2 = titleWords.slice(midPoint).join(' ');
    
    return (
      <div className={className} style={{ width: PAGE_WIDTH, height: PAGE_HEIGHT }}>
        <div className="cover-page">
          <div className="cover-top-banner">
            <span className="banner-logo">RRC</span>
            <span className="banner-issue">{SYMBOLS.NUMBER_SIGN} {issueNumber}</span>
          </div>

          <div className="cover-title-area">
            <h1 className="cover-title">
              <span className="title-line1">{titleLine1}</span>
              {titleLine2 && <span className="title-line2">{titleLine2}</span>}
            </h1>
            <div className="title-sub">A RIBBON RIDER COMIC</div>
          </div>

          <div className="cover-image-container">
            {coverImage ? (
              <img src={coverImage} alt="Cover" className="cover-img" crossOrigin="anonymous" />
            ) : (
              <div className="cover-placeholder">COVER</div>
            )}
            <div className="cover-overlay"></div>
          </div>

          <div className="cover-tagline">A TIME TRAVEL ADVENTURE</div>
          <div className="cover-bottom-banner">INTERACTIVE GRAPHIC NOVEL</div>
        </div>
      </div>
    );
  };

  // STORY PAGE
  const renderStoryPage = (pageData, pageNumber, forExport = false) => {
    const { panels: pagePanels, layout, startIndex } = pageData;
    const className = forExport ? 'comic-page comic-page-render' : 'comic-page comic-page-preview';
    
    return (
      <div className={className} style={{ width: PAGE_WIDTH, height: PAGE_HEIGHT }}>
        <div className="story-page">
          <div className="page-header">
            <span className="header-title">RIBBON RIDER COMICS</span>
            <span className="header-page">PAGE {pageNumber}</span>
          </div>

          <div className="panels-area">
            {pagePanels.map((panel, idx) => {
              const pos = layout[idx] || layout[0];
              const globalIdx = startIndex + idx;
              const playerAction = panel.playerAction; // Now stored directly on panel
              const narrative = panel.content;
              
              return (
                <div 
                  key={panel.id || idx}
                  className="panel-container"
                  style={{
                    left: `${pos.x}%`,
                    top: `${pos.y}%`,
                    width: `${pos.w}%`,
                    height: `${pos.h}%`,
                  }}
                >
                  <div className="panel-frame">
                    <div className="panel-image-area">
                      {panel.imageUrl ? (
                        <img src={panel.imageUrl} alt={`Panel ${globalIdx + 1}`} className="panel-img" crossOrigin="anonymous" />
                      ) : (
                        <div className="panel-placeholder">{globalIdx + 1}</div>
                      )}
                      <div className="panel-halftone"></div>
                    </div>

                    <div className="panel-num">{globalIdx + 1}</div>

                    {playerAction && (
                      <div className="action-box">
                        <span className="action-arrow">{SYMBOLS.TRIANGLE_RIGHT}</span>
                        <span className="action-text"><MarkdownText>{playerAction}</MarkdownText></span>
                      </div>
                    )}

                    {narrative && (
                      <div className="narrative-box">
                        <MarkdownText>{narrative}</MarkdownText>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          <div className="page-footer"></div>
        </div>
      </div>
    );
  };

  // BACK COVER
  const renderBackCover = (forExport = false) => {
    const className = forExport ? 'comic-page comic-page-render' : 'comic-page comic-page-preview';
    
    return (
      <div className={className} style={{ width: PAGE_WIDTH, height: PAGE_HEIGHT }}>
        <div className="back-page" style={{ borderColor: resultConfig.color }}>
          <div className="corner tl"></div>
          <div className="corner tr"></div>
          <div className="corner bl"></div>
          <div className="corner br"></div>

          <div className="result-icon" style={{ color: resultConfig.color }}>{resultConfig.icon}</div>
          <h2 className="result-title" style={{ color: resultConfig.color }}>{resultConfig.title}</h2>
          <div className="result-line" style={{ backgroundColor: resultConfig.color }}></div>

          <div className="stats">
            <div className="stat">
              <span className="stat-val" style={{ color: resultConfig.color }}>{storyPanels.length}</span>
              <span className="stat-label">PANELS</span>
            </div>
            <div className="stat">
              <span className="stat-val" style={{ color: resultConfig.color }}>{panels.filter(p => p.playerAction).length}</span>
              <span className="stat-label">ACTIONS</span>
            </div>
            <div className="stat">
              <span className="stat-val" style={{ color: resultConfig.color }}>{totalPages - 2}</span>
              <span className="stat-label">PAGES</span>
            </div>
          </div>

          <p className="result-message">
            {resultConfig.success 
              ? "History has been altered. The timeline will never be the same."
              : "Some moments resist change. The timeline remains intact."}
          </p>

          <div className="the-end" style={{ color: resultConfig.color }}>THE END</div>

          <div className="back-footer">
            <div className="back-brand">RIBBON RIDER COMICS</div>
            <div className="back-tagline">Your story. Your choices. Your adventure.</div>
          </div>
        </div>
      </div>
    );
  };

  const renderCurrentPreviewPage = () => {
    const pageData = comicPages[currentPage];
    if (!pageData) return null;
    if (pageData.type === 'cover') return renderCover(false);
    if (pageData.type === 'back') return renderBackCover(false);
    return renderStoryPage(pageData, currentPage, false);
  };

  return (
    <div className="comic-export-overlay">
      <div className="comic-export-modal">
        <div className="modal-header">
          <h2 className="modal-title"><BookOpen size={20} strokeWidth={2.5} /> RIBBON RIDER COMICS</h2>
        </div>

        {!imagesLoaded && (
          <div className="loading-area">
            <div className="spinner"></div>
            <p>Loading images...</p>
          </div>
        )}

        {imagesLoaded && (
          <>
            <div className="preview-area">
              <div className="preview-wrapper" ref={previewWrapperRef}>
                <div 
                  className="preview-scaler"
                  style={{
                    width: PAGE_WIDTH * previewScale,
                    height: PAGE_HEIGHT * previewScale,
                  }}
                >
                  <div style={{ transform: `scale(${previewScale})`, transformOrigin: 'top left' }}>
                    {renderCurrentPreviewPage()}
                  </div>
                </div>
              </div>

              <div className="nav-bar">
                <button className="nav-btn" onClick={() => setCurrentPage(p => Math.max(0, p - 1))} disabled={currentPage === 0}>
                  &lsaquo; PREV
                </button>
                <span className="nav-info">{currentPage + 1} / {totalPages}</span>
                <button className="nav-btn" onClick={() => setCurrentPage(p => Math.min(totalPages - 1, p + 1))} disabled={currentPage === totalPages - 1}>
                  NEXT &rsaquo;
                </button>
              </div>
            </div>

            <div className="actions-bar">
              {isGenerating ? (
                <div className="progress-area">
                  <div className="progress-bar"><div className="progress-fill" style={{ width: `${exportProgress}%` }}></div></div>
                  <span className="progress-text">Generating PDF... {Math.round(exportProgress)}%</span>
                </div>
              ) : (
                <>
                  <button className="action-btn primary" onClick={generatePDF}><Download size={16} strokeWidth={2.5} /> Save Comic Book</button>
                  <button className="action-btn secondary" onClick={() => downloadPageAsPNG(currentPage)}><ImageIcon size={16} strokeWidth={2.5} /> Save Page</button>
                  <button className="action-btn tertiary" onClick={onClose}><X size={16} strokeWidth={2.5} /> Close</button>
                </>
              )}
            </div>
          </>
        )}

        <div className="hidden-area" ref={pagesContainerRef}>
          {imagesLoaded && comicPages.map((pageData, idx) => {
            if (pageData.type === 'cover') return <React.Fragment key="cover">{renderCover(true)}</React.Fragment>;
            if (pageData.type === 'back') return <React.Fragment key="back">{renderBackCover(true)}</React.Fragment>;
            return <React.Fragment key={`page-${idx}`}>{renderStoryPage(pageData, idx, true)}</React.Fragment>;
          })}
        </div>
      </div>
    </div>
  );
};

export default ComicBookExport;